import React from "react";
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, TableContainer } from "@mui/material";
import libFloat from "../../../../util/libFloat";
import { Edit } from "lucide-react";

export function TableItensVenda({ itens, complementos, onAlterar }) {
	const formatCurrency = (val) => libFloat.strToCurrency(val || 0);

	// Total da linha = valor próprio do item + soma dos complementos dele (regra sz-foods,
	// REGRAS-GRADE.md): Grade zera o pai e o valor fica todo no complemento; Subitem consolida
	// no pai e zera o complemento; Normal com Adicional soma os dois. A fórmula antiga aqui
	// ("se tem complemento, ignora o pai") só coincidia com Grade — pra Normal+Adicional ela
	// descartava o preço base do produto.
	const calcularTotalLinha = (item, itemComps) => {
		const valorProprio = Number(item.valor_total ?? item.valortotal ?? item.valor ?? 0);
		const qtdeItem = Number(item.qtde ?? item.quantidade ?? 1);
		const valorComplementos = itemComps.reduce((s, c) => s + Number(c.valor ?? c.valortotal ?? 0) * qtdeItem, 0);
		return valorProprio + valorComplementos;
	};

	const totalGeral = (itens || []).reduce((acc, item) => {
		const itemComps = (complementos || []).filter((c) => c.id_venda_item === item.id_venda_item);
		return acc + calcularTotalLinha(item, itemComps);
	}, 0);

	return (
		<div className="mb-2">
			<div className="flex justify-between items-end mb-2">
				<h2 className="text-lg font-bold text-[#1e3a8a] uppercase tracking-wide">Produtos</h2>
				<button
					className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors uppercase"
					title="Alterar Produtos"
					onClick={onAlterar}
				>
					<Edit size={14} /> Alterar Produtos
				</button>
			</div>

			<TableContainer component={Paper} className="shadow-none border border-gray-200 rounded-none">
				<Table size="small">
					<TableHead className="color-table">
						<TableRow>
							<TableCell style={{ color: "#fff", fontWeight: "bold", width: "5%" }}>#</TableCell>
							<TableCell style={{ color: "#fff", fontWeight: "bold", width: "45%" }}>
								Produto / Complemento
							</TableCell>
							<TableCell align="center" style={{ color: "#fff", fontWeight: "bold", width: "10%" }}>
								Qtde
							</TableCell>
							<TableCell align="right" style={{ color: "#fff", fontWeight: "bold", width: "15%" }}>
								Valor Unit.
							</TableCell>
							<TableCell align="right" style={{ color: "#fff", fontWeight: "bold", width: "15%" }}>
								Total
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{itens?.length > 0 ? (
							itens.map((item, index) => {
								// Encontra os complementos vinculados a este item
								const itemComps = (complementos || []).filter(
									(c) => c.id_venda_item === item.id_venda_item,
								);
								const hasComps = itemComps.length > 0;
								const qtdeItem = Number(item.qtde ?? item.quantidade ?? 1);
								
								// Identifica se é Grade verificando o TIPO do complemento ou se o pai tem valor menor/igual a zero (fallback)
								const hasGradeComp = hasComps && (
									itemComps.some((c) => c.tipo?.toUpperCase() === "GRADE" || c.TIPO?.toUpperCase() === "GRADE") ||
									Number(item.valor_total ?? item.valortotal ?? item.valor ?? 0) <= 0
								);

								return (
									<React.Fragment key={index}>
										{/* Linha do Produto — sempre fundo azul (identifica venda_item) */}
										<TableRow hover style={{ backgroundColor: "#eff6ff" }}>
											<TableCell style={{ fontWeight: "bold", color: "#1e3a8a" }}>
												{index + 1}
											</TableCell>
											<TableCell style={{ fontWeight: "bold", color: "#1e3a8a" }}>
												{item.nome || item.descricao || "—"}
											</TableCell>
											<TableCell align="center" style={{ fontWeight: "bold", color: "#1e3a8a" }}>
												{hasGradeComp ? "" : hasComps ? `${qtdeItem}x` : qtdeItem}
											</TableCell>
											<TableCell align="right" style={{ fontWeight: "bold", color: "#1e3a8a" }}>
												{hasGradeComp
													? ""
													: formatCurrency(
															item.valor_unitario ??
																item.valorunitario ??
																item.valor_unit ??
																0,
														)}
											</TableCell>
											<TableCell align="right" style={{ fontWeight: "bold", color: "#1e3a8a" }}>
												{hasGradeComp
													? ""
													: formatCurrency(
															item.valor_total ?? item.valortotal ?? item.valor ?? 0,
														)}
											</TableCell>
										</TableRow>

										{/* Linhas dos Complementos — sem cor de fundo, valor cru + "× qtde do pai" quando > 1 */}
										{itemComps.map((comp, compIdx) => (
											<TableRow key={`comp-${compIdx}`} hover>
												<TableCell></TableCell>
												<TableCell style={{ paddingLeft: "30px", color: "#4b5563" }}>
													{comp.nome || comp.descricao || "—"}
												</TableCell>
												<TableCell align="center" style={{ color: "#4b5563" }}>
													{comp.qtde ?? comp.quantidade ?? 1}
												</TableCell>
												<TableCell align="right" style={{ color: "#4b5563" }}>
													{qtdeItem > 1 && (
														<span className="mr-1 text-[10px] font-bold text-gray-400">
															{qtdeItem} ×
														</span>
													)}
													{formatCurrency(
														comp.valorunit || comp.valor_unit || comp.valor || 0,
													)}
												</TableCell>
												<TableCell align="right" style={{ color: "#4b5563" }}>
													{formatCurrency(
														Number(comp.valor || comp.valortotal || 0) * qtdeItem,
													)}
												</TableCell>
											</TableRow>
										))}

										{/* Linha do Desconto do Item — se houver desconto cadastrado no item pai */}
										{Number(item.desconto ?? item.DESCONTO ?? 0) > 0 && (
											<TableRow hover>
												<TableCell></TableCell>
												<TableCell style={{ paddingLeft: "30px", color: "#dc2626" }}>
													(-) DESCONTO
												</TableCell>
												<TableCell align="center" style={{ color: "#dc2626" }}></TableCell>
												<TableCell align="right" style={{ color: "#dc2626" }}></TableCell>
												<TableCell align="right" style={{ color: "#dc2626" }}>
													-{formatCurrency(item.desconto ?? item.DESCONTO ?? 0)}
												</TableCell>
											</TableRow>
										)}

										{/* Total do produto: aparece quando tem complemento ou desconto — soma pai + complementos */}
										{(hasComps || Number(item.desconto ?? item.DESCONTO ?? 0) > 0) && (
											<TableRow>
												<TableCell></TableCell>
												<TableCell
													colSpan={3}
													align="right"
													style={{
														fontWeight: "bold",
														color: "#1e3a8a",
														fontSize: "0.75rem",
														textTransform: "uppercase",
													}}
												>
													Total
												</TableCell>
												<TableCell
													align="right"
													style={{ fontWeight: "bold", color: "#1e3a8a" }}
												>
													{formatCurrency(calcularTotalLinha(item, itemComps))}
												</TableCell>
											</TableRow>
										)}
									</React.Fragment>
								);
							})
						) : (
							<TableRow>
								<TableCell colSpan={5} align="center" style={{ padding: "20px" }}>
									Nenhum produto encontrado nesta movimentação.
								</TableCell>
							</TableRow>
						)}

						{/* Total Geral: soma de todos os itens (item + complementos, já com a
						    multiplicação certa) — os valores de cada linha acima ficam sempre "crus",
						    exatamente como estão gravados no banco, sem tentar se explicar sozinhos. */}
						{itens?.length > 0 && (
							<TableRow style={{ backgroundColor: "#f3f4f6" }}>
								<TableCell
									colSpan={4}
									align="right"
									style={{
										fontWeight: "bold",
										textTransform: "uppercase",
										fontSize: "0.75rem",
										color: "#374151",
									}}
								>
									Total Geral
								</TableCell>
								<TableCell align="right" style={{ fontWeight: "bold", color: "#1e3a8a" }}>
									{formatCurrency(totalGeral)}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
}
